import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Calendar, Users, BookOpen, Plus, Eye, Menu, ChevronDown } from 'lucide-react';
import CompetitionAnalysis from '../CompetitionAnalysis';
import EventEnrollments from '../EventEnrollments';
import StarRating from '../StarRating';
import { useDashboard } from './DashboardContext';

export const TeacherDashboard = () => {
  const {
    user, activeRole, users, events,
    activeTeacherTab, setActiveTeacherTab,
    teacherMobileMenuOpen, setTeacherMobileMenuOpen,
    teacherEvents, myEnrollments, matchAnalyses, detailedMatchAnalyses,
    newMatchAnalysis, setNewMatchAnalysis,
    newDetailedAnalysis, setNewDetailedAnalysis,
    analysisFilters, setAnalysisFilters,
    showCreateDetailedAnalysis, setShowCreateDetailedAnalysis,
    selectedAnalysis, setSelectedAnalysis,
    selectedDetailedAnalysis, setSelectedDetailedAnalysis,
    selectedEnrollmentDetails, setSelectedEnrollmentDetails,
    createMatchAnalysis, createDetailedMatchAnalysis,
    filterMatchAnalyses, clearAnalysisFilters,
    sendAnalysisEmail,
    fetchTeacherEventEnrollments,
    fetchStudyMaterials, setSelectedEventForMaterials,
    formatDate, formatDateRange
  } = useDashboard();

  return (
    <Tabs value={activeTeacherTab} onValueChange={setActiveTeacherTab} className="space-y-6" data-testid="teacher-dashboard">
      <TabsList className="hidden md:grid w-full grid-cols-6">
        <TabsTrigger value="competition" data-testid="competition-tab">Analysis di Gara</TabsTrigger>
        <TabsTrigger value="analysis" data-testid="analysis-tab">Analysis</TabsTrigger>
        <TabsTrigger value="create" data-testid="create-tab">Crea</TabsTrigger>
        <TabsTrigger value="detailed" data-testid="detailed-tab">Dettagliate</TabsTrigger>
        <TabsTrigger value="events" data-testid="teacher-events-tab">I Miei Eventi</TabsTrigger>
        <TabsTrigger value="enrollments" data-testid="teacher-enrollments-tab">Le Mie Iscrizioni</TabsTrigger>
      </TabsList>
      
      {/* Mobile Menu */}
      <div className="md:hidden relative">
        <button
          onClick={() => setTeacherMobileMenuOpen(!teacherMobileMenuOpen)}
          className="w-full flex items-center justify-between px-4 py-3 bg-white border rounded-lg shadow-sm"
          data-testid="teacher-mobile-menu-button"
        >
          <div className="flex items-center space-x-2">
            <Menu className="w-5 h-5 text-gray-600" />
            <span className="text-gray-600">Scegli sezione</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="font-medium text-red-600">
              {activeTeacherTab === 'competition' && 'Analysis di Gara'}
              {activeTeacherTab === 'analysis' && 'Analysis'}
              {activeTeacherTab === 'create' && 'Crea'}
              {activeTeacherTab === 'detailed' && 'Dettagliate'}
              {activeTeacherTab === 'events' && 'I Miei Eventi'}
              {activeTeacherTab === 'enrollments' && 'Le Mie Iscrizioni'}
            </span>
            <ChevronDown className={`w-5 h-5 text-gray-600 transition-transform ${teacherMobileMenuOpen ? 'rotate-180' : ''}`} />
          </div>
        </button>
        
        {teacherMobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-50">
            {[
              { value: 'competition', label: 'Analysis di Gara' },
              { value: 'analysis', label: 'Analysis' },
              { value: 'create', label: 'Crea' },
              { value: 'detailed', label: 'Dettagliate' },
              { value: 'events', label: 'I Miei Eventi' },
              { value: 'enrollments', label: 'Le Mie Iscrizioni' }
            ].map((item) => (
              <button
                key={item.value}
                onClick={() => { setActiveTeacherTab(item.value); setTeacherMobileMenuOpen(false); }}
                className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-b last:border-b-0 ${
                  activeTeacherTab === item.value ? 'bg-red-50 text-red-600 font-medium' : 'text-gray-700'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>
      
      {/* Competition Analysis Tab */}
      <TabsContent value="competition" data-testid="competition-analysis-content">
        <CompetitionAnalysis user={user} activeRole={activeRole} />
      </TabsContent>
      
      {/* Analysis Tab */}
      <TabsContent value="analysis" data-testid="teacher-analysis-content">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <BookOpen className="w-5 h-5" />
              <span>Match Analysis Create</span>
            </CardTitle>
            <CardDescription>Match Analysis che hai creato per i tuoi atleti</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium mb-3">Filtri di Ricerca</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="filter-date">Data</Label>
                  <Input id="filter-date" type="date" value={analysisFilters.date}
                    onChange={(e) => setAnalysisFilters(prev => ({...prev, date: e.target.value}))} />
                </div>
                <div>
                  <Label htmlFor="filter-event">Evento</Label>
                  <Input id="filter-event" value={analysisFilters.event}
                    onChange={(e) => setAnalysisFilters(prev => ({...prev, event: e.target.value}))} placeholder="Cerca per evento" />
                </div>
                <div>
                  <Label htmlFor="filter-athlete">Atleta</Label>
                  <Input id="filter-athlete" value={analysisFilters.athlete}
                    onChange={(e) => setAnalysisFilters(prev => ({...prev, athlete: e.target.value}))} placeholder="Cerca per atleta" />
                </div>
                <div className="flex items-end">
                  <Button type="button" variant="outline" onClick={clearAnalysisFilters} className="w-full">Pulisci Filtri</Button>
                </div>
              </div>
            </div>

            {filterMatchAnalyses(matchAnalyses).length === 0 ? (
              <p className="text-gray-500 text-center py-8" data-testid="no-teacher-analysis">Non hai ancora creato nessuna Match Analysis</p>
            ) : (
              <div className="space-y-4" data-testid="teacher-analysis-list">
                {filterMatchAnalyses(matchAnalyses).map((analysis) => (
                  <div key={analysis.id} className="border rounded-lg p-4 hover:bg-blue-50 transition-colors cursor-pointer"
                    data-testid={`teacher-analysis-${analysis.id}`} onClick={() => setSelectedAnalysis(analysis)}>
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold text-blue-700" data-testid={`teacher-analysis-event-${analysis.id}`}>{analysis.event_title}</h3>
                      <span className="text-sm text-gray-500" data-testid={`teacher-analysis-date-${analysis.id}`}>{formatDate(analysis.created_at)}</span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2" data-testid={`teacher-analysis-athlete-${analysis.id}`}>Atleta: {analysis.athlete_name}</p>
                    <div className="bg-gray-50 rounded p-3" data-testid={`teacher-analysis-content-${analysis.id}`}>
                      <p className="text-gray-700 line-clamp-3">{analysis.content}</p>
                    </div>
                    <div className="mt-2 flex justify-end">
                      <Button variant="outline" size="sm"><Eye className="w-4 h-4 mr-1" />Visualizza Dettagli</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>
      
      {/* Create Analysis Tab */}
      <TabsContent value="create" data-testid="create-analysis-content">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Plus className="w-5 h-5" />
              <span>Crea Match Analysis</span>
            </CardTitle>
            <CardDescription>Fornisci feedback personalizzato a un atleta</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={createMatchAnalysis} className="space-y-4" data-testid="create-analysis-form">
              <div className="space-y-2">
                <Label>Tipo Analisi</Label>
                <div className="flex space-x-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input type="radio" name="analysis_type" checked={!newMatchAnalysis.is_private_lesson}
                      onChange={() => setNewMatchAnalysis({...newMatchAnalysis, is_private_lesson: false, athlete_name: '', athlete_surname: ''})}
                      className="text-blue-600" />
                    <span>Evento</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input type="radio" name="analysis_type" checked={newMatchAnalysis.is_private_lesson}
                      onChange={() => setNewMatchAnalysis({...newMatchAnalysis, is_private_lesson: true, event_id: '', athlete_id: ''})}
                      className="text-blue-600" />
                    <span>Lezione Privata</span>
                  </label>
                </div>
              </div>

              {!newMatchAnalysis.is_private_lesson ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="event_id">Evento *</Label>
                    <Select value={newMatchAnalysis.event_id} onValueChange={(value) => setNewMatchAnalysis({...newMatchAnalysis, event_id: value})}>
                      <SelectTrigger><SelectValue placeholder="Seleziona evento" /></SelectTrigger>
                      <SelectContent>
                        {events.map((event) => (
                          <SelectItem key={event.id} value={event.id}>{event.title} - {formatDateRange(event.start_date, event.end_date, event.date_tbd)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="athlete_id">Atleta *</Label>
                    <Select value={newMatchAnalysis.athlete_id} onValueChange={(value) => setNewMatchAnalysis({...newMatchAnalysis, athlete_id: value})}>
                      <SelectTrigger><SelectValue placeholder="Seleziona atleta" /></SelectTrigger>
                      <SelectContent>
                        {users.filter(u => {
                          const userRoles = u.roles || (u.role ? [u.role] : []);
                          return (userRoles.includes('atleta') || u.role === 'atleta') && u.approved;
                        }).map((u) => (
                          <SelectItem key={u.id} value={u.id}>{u.name} {u.surname}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="athlete_name">Nome Atleta *</Label>
                    <Input id="athlete_name" value={newMatchAnalysis.athlete_name}
                      onChange={(e) => setNewMatchAnalysis({...newMatchAnalysis, athlete_name: e.target.value})} placeholder="Nome dell'atleta" data-testid="athlete-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="athlete_surname">Cognome Atleta *</Label>
                    <Input id="athlete_surname" value={newMatchAnalysis.athlete_surname}
                      onChange={(e) => setNewMatchAnalysis({...newMatchAnalysis, athlete_surname: e.target.value})} placeholder="Cognome dell'atleta" data-testid="athlete-surname-input" />
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="content">Contenuto Analysis</Label>
                <Textarea id="content" value={newMatchAnalysis.content}
                  onChange={(e) => setNewMatchAnalysis({...newMatchAnalysis, content: e.target.value})}
                  placeholder="Scrivi qui la tua analisi della performance dell'atleta..." rows={6} required data-testid="analysis-content-input" />
              </div>
              
              <Button type="submit" className="w-full" data-testid="create-analysis-button">Crea Match Analysis</Button>
            </form>
          </CardContent>
        </Card>
      </TabsContent>
      
      {/* Detailed Analysis Tab */}
      <TabsContent value="detailed" data-testid="teacher-detailed-content">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Plus className="w-5 h-5" />
                <span>Crea Match Analysis Dettagliata</span>
              </CardTitle>
              <CardDescription>Valuta l'atleta con punteggi specifici per ogni aspetto tecnico</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={createDetailedMatchAnalysis} className="space-y-6" data-testid="create-detailed-analysis-form">
                <div className="space-y-2">
                  <Label>Tipo Analisi</Label>
                  <div className="flex space-x-4">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input type="radio" name="detailed_analysis_type" checked={!newDetailedAnalysis.is_private_lesson}
                        onChange={() => setNewDetailedAnalysis({...newDetailedAnalysis, is_private_lesson: false, athlete_name: '', athlete_surname: ''})}
                        className="text-blue-600" />
                      <span>Evento</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input type="radio" name="detailed_analysis_type" checked={newDetailedAnalysis.is_private_lesson}
                        onChange={() => setNewDetailedAnalysis({...newDetailedAnalysis, is_private_lesson: true, event_id: '', athlete_id: ''})}
                        className="text-blue-600" />
                      <span>Lezione Privata</span>
                    </label>
                  </div>
                </div>

                {!newDetailedAnalysis.is_private_lesson ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="detailed_event_id">Evento *</Label>
                      <Select value={newDetailedAnalysis.event_id} onValueChange={(value) => setNewDetailedAnalysis({...newDetailedAnalysis, event_id: value})}>
                        <SelectTrigger><SelectValue placeholder="Seleziona evento" /></SelectTrigger>
                        <SelectContent>
                          {events.map((event) => (
                            <SelectItem key={event.id} value={event.id}>{event.title} - {formatDateRange(event.start_date, event.end_date, event.date_tbd)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="detailed_athlete_id">Atleta *</Label>
                      <Select value={newDetailedAnalysis.athlete_id} onValueChange={(value) => setNewDetailedAnalysis({...newDetailedAnalysis, athlete_id: value})}>
                        <SelectTrigger><SelectValue placeholder="Seleziona atleta" /></SelectTrigger>
                        <SelectContent>
                          {users.filter(u => {
                            const userRoles = u.roles || (u.role ? [u.role] : []);
                            return (userRoles.includes('atleta') || u.role === 'atleta') && u.approved;
                          }).map((u) => (
                            <SelectItem key={u.id} value={u.id}>{u.name} {u.surname}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="detailed_athlete_name">Nome Atleta *</Label>
                      <Input id="detailed_athlete_name" value={newDetailedAnalysis.athlete_name}
                        onChange={(e) => setNewDetailedAnalysis({...newDetailedAnalysis, athlete_name: e.target.value})} placeholder="Nome dell'atleta" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="detailed_athlete_surname">Cognome Atleta *</Label>
                      <Input id="detailed_athlete_surname" value={newDetailedAnalysis.athlete_surname}
                        onChange={(e) => setNewDetailedAnalysis({...newDetailedAnalysis, athlete_surname: e.target.value})} placeholder="Cognome dell'atleta" />
                    </div>
                  </div>
                )}
                
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Valutazione Tecnica (1-10)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { key: 'tempo', label: 'Tempo' },
                      { key: 'ritmo', label: 'Ritmo' },
                      { key: 'posizione_piedi', label: 'Posizione dei Piedi' },
                      { key: 'equilibrio', label: 'Equilibrio' },
                      { key: 'correttezza_eleganza', label: 'Correttezza ed Eleganza' },
                      { key: 'spinta_slancio', label: 'Spinta e Slancio' },
                      { key: 'rotazione_testa', label: 'Rotazione e Movimenti della Testa' },
                      { key: 'eleganza_estetica', label: 'Eleganza ed Aspetto Estetico' },
                      { key: 'fluidita_agilita', label: 'Fluidit\u00e0, Agilit\u00e0 e Scioltezza' },
                      { key: 'espressivita_carattere', label: 'Espressivit\u00e0 e Carattere' },
                      { key: 'musicalita_interpretazione', label: 'Musicalit\u00e0 e Interpretazione del Brano' },
                      { key: 'stile_portamento', label: 'Stile, Portamento e Interpretazione' }
                    ].map(({ key, label }) => (
                      <div key={key} className="space-y-2">
                        <StarRating label={label} value={newDetailedAnalysis.scores[key]}
                          onChange={(rating) => setNewDetailedAnalysis({
                            ...newDetailedAnalysis, scores: { ...newDetailedAnalysis.scores, [key]: rating }
                          })} />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex space-x-2">
                  <Button type="button" variant="outline" onClick={() => setShowCreateDetailedAnalysis(false)} data-testid="cancel-detailed-analysis-button">Annulla</Button>
                  <Button type="submit" data-testid="create-detailed-analysis-button">Crea Analysis Dettagliata</Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* List of Created Detailed Analyses */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <BookOpen className="w-5 h-5" />
                <span>Le Mie Analysis Dettagliate Create</span>
              </CardTitle>
              <CardDescription>Analisi dettagliate che hai creato per i tuoi atleti</CardDescription>
            </CardHeader>
            <CardContent>
              {detailedMatchAnalyses.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Non hai ancora creato nessuna analisi dettagliata</p>
              ) : (
                <div className="space-y-4">
                  {detailedMatchAnalyses.map((analysis) => (
                    <div key={analysis.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="font-semibold text-lg">{analysis.event_title}</h3>
                            {analysis.is_private_lesson && <Badge className="bg-purple-500">Lezione Privata</Badge>}
                          </div>
                          <p className="text-sm text-gray-600 mb-1">Atleta: {analysis.athlete_name}</p>
                          <span className="text-sm text-gray-500">{formatDate(analysis.created_at)}</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <div className="text-right mr-4">
                            <div className="text-2xl font-bold text-green-600">{analysis.total_score}/10</div>
                            <div className="text-sm text-gray-500">Punteggio</div>
                          </div>
                          <div className="flex flex-col space-y-2">
                            <Button variant="outline" size="sm" onClick={() => setSelectedDetailedAnalysis(analysis)} className="flex items-center">
                              <Eye className="w-4 h-4 mr-1" /> Visualizza
                            </Button>
                            {analysis.is_private_lesson && (
                              <Button variant="outline" size="sm" onClick={() => sendAnalysisEmail(analysis.id)}
                                className="flex items-center bg-blue-50 hover:bg-blue-100 text-blue-600 border-blue-200">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                Invia Email
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {Object.entries(analysis.scores || {}).map(([key, value]) => (
                          <div key={key} className="text-center p-3 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                            <div className="text-xs text-blue-600 mb-2 font-medium">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
                            <div className="text-lg font-bold text-blue-800">{value}/10</div>
                          </div>
                        ))}
                      </div>
                      {analysis.notes && (
                        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200 mt-4">
                          <h4 className="font-medium text-yellow-800 mb-2">Note:</h4>
                          <p className="text-yellow-700 whitespace-pre-wrap">{analysis.notes}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </TabsContent>

      {/* Teacher Events Tab */}
      <TabsContent value="events" data-testid="teacher-events-content">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Calendar className="w-5 h-5" />
              <span>I Miei Eventi come Insegnante</span>
            </CardTitle>
            <CardDescription>Eventi in cui sei assegnato come insegnante</CardDescription>
          </CardHeader>
          <CardContent>
            {teacherEvents.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Non sei ancora assegnato a nessun evento</p>
              </div>
            ) : (
              <div className="space-y-4">
                {teacherEvents.map((event) => (
                  <div key={event.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{event.title}</h3>
                        <p className="text-gray-600 mb-2">{event.description}</p>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span>{formatDateRange(event.start_date, event.end_date, event.date_tbd)}</span>
                          <span>{event.location}</span>
                          <Badge variant="secondary">{event.type}</Badge>
                          <span>{event.enrolled_count} iscritti</span>
                        </div>
                      </div>
                      <EventEnrollments eventId={event.id} eventTitle={event.title}
                        fetchEnrollments={fetchTeacherEventEnrollments} userRole="teacher" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      {/* Teacher Enrollments Tab */}
      <TabsContent value="enrollments" data-testid="teacher-enrollments-content">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Calendar className="w-5 h-5" />
              <span>Le Mie Iscrizioni come Discente</span>
            </CardTitle>
            <CardDescription>Eventi e corsi a cui ti sei iscritto come partecipante</CardDescription>
          </CardHeader>
          <CardContent>
            {myEnrollments.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Non sei iscritto a nessun evento</p>
                <p className="text-sm text-gray-400 mt-2">Vai alla homepage per iscriverti agli eventi disponibili</p>
              </div>
            ) : (
              <div className="space-y-4">
                {myEnrollments.map((enrollment) => {
                  const event = events.find(e => e.id === enrollment.event_id);
                  if (!event) return null;
                  return (
                    <div key={enrollment.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">{event.title}</h3>
                          <p className="text-gray-600 mb-2">{event.description}</p>
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span>{formatDateRange(event.start_date, event.end_date, event.date_tbd)}</span>
                            <span>{event.location}</span>
                            <Badge variant={enrollment.status === 'approved' ? 'default' : 'secondary'}
                              className={enrollment.status === 'approved' ? 'bg-green-500' : 'bg-yellow-500'}>
                              {enrollment.status === 'approved' ? 'Approvato' : 'In Attesa'}
                            </Badge>
                            {enrollment.paid && <Badge className="bg-blue-500">Pagato</Badge>}
                          </div>
                        </div>
                        <Button variant="outline" size="sm" onClick={async () => {
                          setSelectedEnrollmentDetails(event);
                          await fetchStudyMaterials(event.id);
                        }}>
                          <Eye className="w-4 h-4 mr-1" /> Dettagli
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
};
